import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that clamps the product description height
 * to match the adjacent (sibling) group column, with a
 * "Read more" / "Show less" toggle.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content - The scrollable content wrapper.
 * @property {HTMLElement} fade - The gradient overlay + button wrapper.
 * @property {HTMLButtonElement} toggle - The toggle button.
 *
 * @extends {Component<Refs>}
 */
class DescriptionReadMore extends Component {
  requiredRefs = ['content', 'fade', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {HTMLElement | null} */
  #referenceColumn = null;

  /** @type {boolean} */
  #expanded = false;

  /** @type {number} */
  #clampHeight = 0;

  connectedCallback() {
    super.connectedCallback();

    const parentGroup = this.closest('.group-block');
    if (!parentGroup) return;

    // Find the next sibling group block (the right column)
    let sibling = parentGroup.nextElementSibling;
    while (sibling && !sibling.classList.contains('group-block')) {
      sibling = sibling.nextElementSibling;
    }
    this.#referenceColumn = sibling;

    if (this.#referenceColumn) {
      this.#observer = new ResizeObserver(() => this.#update());
      this.#observer.observe(this.#referenceColumn);
    }

    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);

    // Initial update after layout settles
    requestAnimationFrame(() => this.#update());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onBreakpointChange = () => {
    this.#expanded = false;
    this.dataset.expanded = '';
    this.refs.toggle.textContent = this.refs.toggle.dataset.moreText || 'Read more...';
    this.refs.content.style.maxHeight = '';
    this.#update();
  };

  /**
   * Resolve --mobile-max-height CSS custom property to pixels
   * by temporarily applying it to a hidden element.
   * @returns {number}
   */
  #resolveMobileMaxHeight() {
    const value = getComputedStyle(this).getPropertyValue('--mobile-max-height')?.trim();
    if (!value) return 0;

    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;height:${value};`;
    this.appendChild(probe);
    const px = probe.offsetHeight;
    probe.remove();
    return px;
  }

  #update() {
    const { content, fade } = this.refs;
    const isDesktop = mediaQueryLarge.matches;

    if (this.#expanded) return;

    if (!isDesktop) {
      // Mobile: clamp to the --mobile-max-height CSS custom property
      const mobileMax = this.#resolveMobileMaxHeight();
      if (mobileMax > 0) {
        this.#clampHeight = mobileMax;
        content.style.maxHeight = `${mobileMax}px`;
        const overflows = content.scrollHeight > mobileMax + 2;
        fade.hidden = !overflows;
        if (!overflows) content.style.maxHeight = '';
      } else {
        content.style.maxHeight = '';
        fade.hidden = true;
      }
      return;
    }

    if (!this.#referenceColumn) return;

    const refHeight = this.#referenceColumn.offsetHeight;
    this.#clampHeight = refHeight;

    content.style.maxHeight = `${refHeight}px`;

    // Check if content actually overflows
    const overflows = content.scrollHeight > refHeight + 2; // 2px tolerance
    fade.hidden = !overflows;

    if (!overflows) {
      content.style.maxHeight = '';
    }
  }

  /**
   * Toggle between clamped and expanded states
   */
  toggle() {
    const { content, fade, toggle: btn } = this.refs;

    this.#expanded = !this.#expanded;
    this.dataset.expanded = this.#expanded ? 'true' : 'false';

    if (this.#expanded) {
      content.style.maxHeight = `${content.scrollHeight}px`;
      btn.textContent = btn.dataset.lessText || 'Show less';
      // After transition, remove max-height to allow natural flow
      content.addEventListener(
        'transitionend',
        () => {
          if (this.#expanded) content.style.maxHeight = 'none';
        },
        { once: true }
      );
    } else {
      // Re-clamp: set current height first, then transition to clamped
      content.style.maxHeight = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        content.style.maxHeight = `${this.#clampHeight}px`;
      });
      btn.textContent = btn.dataset.moreText || 'Read more...';
      fade.hidden = false;
    }
  }
}

if (!customElements.get('description-read-more')) {
  customElements.define('description-read-more', DescriptionReadMore);
}
